import { clsx } from 'clsx';
import { JSX } from 'react';
import moment from 'moment-timezone';
import { useNotify, useRedirect } from 'react-admin';

import { AdminNotification, DealerType } from '@types';
import { adminHttp, NotificationEntityTypes } from '@network';
import { axiosErrorToString, rbac } from '@utils';
import cls from './notifications.module.css';

const dealerTypeLink = {
  [DealerType.BUYER]: 'dealer',
  [DealerType.RETAILER]: 'dealer-retailer',
  [DealerType.ASSOCIATE]: 'dealer-associate',
};

interface NotificationsItemProps {
  entity: NotificationEntityTypes;
  items: AdminNotification[];
  refreshNotifications: () => void;
}

export const NotificationsItem = ({
  entity, items, refreshNotifications,
}: NotificationsItemProps) => {
  const redirect = useRedirect();
  const notify = useNotify();
  const admin = rbac.getAdmin();

  const creditCodesMap: Record<string, boolean> = {};
  const financeCostsMap: Record<string, boolean> = {};
  const financeWithdrawalsMap: Record<string, boolean> = {};
  const financeDepositsMap: Record<string, boolean> = {};
  const partnerNamesMap: Record<string, boolean> = {};

  const allRead = items.every(item => item.isRead);
  let createdAt = '';

  for (const item of items) {
    if (item.creditCode) {
      creditCodesMap[item.creditCode] = true;
    }
    if (item.financeCost) {
      financeCostsMap[item.financeCost.code] = true;
    }
    if (item.financeWithdrawal) {
      financeWithdrawalsMap[item.financeWithdrawal.code] = true;
    }
    if (item.financeDeposit) {
      financeDepositsMap[item.financeDeposit.code] = true;
    }
    if (item.manager) {
      const name = item.manager.dealer?.name || item.manager.name;
      if (name) {
        partnerNamesMap[name] = true;
      }
    }
    if (!createdAt) {
      createdAt = moment(item.enabledAt ?? item.createdAt)
        .tz('America/Mexico_City')
        .locale('mx')
        .format('D MMM YYYY, HH:mm');
    }
  }

  const creditCodes = Object.keys(creditCodesMap);
  const financeCodes = Object.keys(financeCostsMap);
  const financeWithdrawals = Object.keys(financeWithdrawalsMap);
  const financeDeposits = Object.keys(financeDepositsMap);
  const partnerNames = Object.keys(partnerNamesMap);
  const ticketFrom = items[0].ticketFrom;
  const manager = items[0].manager;
  const creditCode = items[0].creditCode;
  const creditId = items[0].creditId;
  const userId = items[0].userId;
  const userName = items[0].userName ?? '';
  const userCode = items[0].userCode ?? '';
  const comment = items[0].comment;
  const partnerName = partnerNames.length === 1
    ? partnerNames[0]
    : partnerNames.join(', ');

  const hasCreditArea = rbac.hasAreaCredit();

  const links: Record<NotificationEntityTypes, string> = {
    [NotificationEntityTypes.USER_ASSIGNED]: `/user?displayedFilters=%7B"responsibleAdminId"%3Atrue%7D&filter=%7B"responsibleAdminId"%3A${admin?.id || ''}%7D&order=DESC&page=1&perPage=10&sort=id`,
    [NotificationEntityTypes.USER_CONTACT_OWNER_UPDATED]: `/user?displayedFilters=%7B"contactOwnerId"%3Atrue%7D&filter=%7B"contactOwnerId"%3A${admin?.id || ''}%7D&order=DESC&page=1&perPage=10&sort=id`,
    [NotificationEntityTypes.CREDIT_FILES_PROGRESS]: '/credit?displayedFilters=%7B%22progress%22%3Atrue%7D&filter=%7B%22progress%22%3A%22completed%22%7D&order=DESC&page=1&perPage=10&sort=id',
    [NotificationEntityTypes.CREDIT_STATUS_MODERATION_SIGNED]: '/credit?displayedFilters=%7B%22progress%22%3Atrue%7D&filter=%7B%22status%22%3A%5B%22signed%22%5D%7D&order=ASC&page=1&perPage=10&sort=status',
    [NotificationEntityTypes.NEW_CREDIT]: '/credit?displayedFilters=%7B%22progress%22%3Atrue%7D&filter=%7B%22status%22%3A%5B%22moderation%22%5D%7D&order=DESC&page=1&perPage=10&sort=createdAt',
    [NotificationEntityTypes.NEW_CONTACT_US]: '/feedback/contact',
    [NotificationEntityTypes.CREDIT_STATUS_CX]: '/credit?displayedFilters=%7B%22progress%22%3Atrue%7D&filter=%7B%22status%22%3A%5B%22signed%22%5D%7D&order=ASC&page=1&perPage=10&sort=createdAt',
    [NotificationEntityTypes.CREDIT_STATUS_DELAY_CX]: '/credit?displayedFilters=%7B%7D&filter=%7B%22status%22%3A%5B%22repaid%22%2C%22restructure%22%2C%22delay%22%5D%7D&order=DESC&page=1&perPage=10&sort=createdAt',
    [NotificationEntityTypes.NEW_RESUME]: '/resume?order=DESC&page=1&perPage=25&sort=createdAt',
    [NotificationEntityTypes.NEW_QUOTATION]: '/order?displayedFilters=%7B%7D&filter=%7B%22status%22%3A%5B%22waiting_for_payment%22%5D%7D&order=DESC&page=1&perPage=10&sort=id',
    [NotificationEntityTypes.INVOICE_UPLOADED]: '/credit?displayedFilters=%7B%22invoices%22%3Atrue%7D&filter=%7B%22invoices%22%3A%22uploaded%22%7D&order=DESC&page=1&perPage=10&sort=id',
    [NotificationEntityTypes.CALL_OF_THE_DAY]: '/credit-call?displayedFilters=%7B%7D&filter=%7B%22callDate%22%3A%22overdue%22%7D&order=DESC&page=1&perPage=10&sort=id',
    [NotificationEntityTypes.USER_UPLOADED_DOCUMENTS]: `/credit?displayedFilters=%7B%7D&filter=%7B"creditCodes"%3A"${creditCodes?.join(',') || ''}"%7D&order=DESC&page=1&perPage=10&sort=id`,
    [NotificationEntityTypes.FINANCE_COST_CREATED]: `/finance-cost?displayedFilters=%7B%7D&filter=%7B"q"%3A"${financeCodes?.join('; ') || ''}"%7D&order=DESC&page=1&perPage=10&sort=id`,
    [NotificationEntityTypes.FINANCE_WITHDRAWAL_CREATED]: `/finance-withdrawal?displayedFilters=%7B%7D&filter=%7B"q"%3A"${financeWithdrawals?.join('; ') || ''}"%7D&order=DESC&page=1&perPage=10&sort=id`,
    [NotificationEntityTypes.FINANCE_DEPOSIT_CREATED]: `/finance-deposit?displayedFilters=%7B%7D&filter=%7B"q"%3A"${financeDeposits?.join('; ') || ''}"%7D&order=DESC&page=1&perPage=10&sort=id`,
    [NotificationEntityTypes.TICKET_CREATED]: `/support-ticket?displayedFilters=%7B%7D&filter=%7B"sentBy"%3A"${ticketFrom}"%7D&order=DESC&page=1&perPage=25&sort=id`,
    [NotificationEntityTypes.NEW_PARTNER_REGISTERED]: (manager?.dealer?.id && dealerTypeLink[manager.dealer.type]) ? `/${dealerTypeLink[manager.dealer.type]}/${manager.dealer.id}?tab=3` : '/manager',
    [NotificationEntityTypes.CONTACT_NEXT_CYCLE]: '/user/' + (userId || ''),
    [NotificationEntityTypes.CREDIT_CONTACT_NEXT_CYCLE]: '/credit/' + (creditId || ''),
    // Credit statuses update
    [NotificationEntityTypes.MODERATION]: `/credit/${items[0].creditId}`,
    [NotificationEntityTypes.PRE_SCREENING]: `/credit/${items[0].creditId}`,
    [NotificationEntityTypes.DOC_COLLECTION]: `/credit/${items[0].creditId}`,
    [NotificationEntityTypes.KYC_SCREENING]: `/credit/${items[0].creditId}`,
    [NotificationEntityTypes.POST_SCREENING]: `/credit/${items[0].creditId}`,
    [NotificationEntityTypes.COMPLETED_FILE]: `/credit/${items[0].creditId}`,
    [NotificationEntityTypes.BANK_REVISION]: hasCreditArea && items[0].riskId
      ? `/credit-risk/${items[0].riskId}`
      : `/credit/${items[0].creditId}`,
    [NotificationEntityTypes.APPROVED]: `/credit/${items[0].creditId}/?initial`,
    [NotificationEntityTypes.IN_NEGOTIATIONS]: `/credit/${items[0].creditId}/?initial`,
    [NotificationEntityTypes.CONTRACT_IN_PROCESS]: `/credit/${items[0].creditId}/?contracts`,
    [NotificationEntityTypes.SIGNED]: `/credit/${items[0].creditId}/?initial`,
    [NotificationEntityTypes.REPAID]: `/credit/${items[0].creditId}/?invoices`,
    [NotificationEntityTypes.RESTRUCTURE]: `/credit/${items[0].creditId}/?initial`,
    [NotificationEntityTypes.DELAY]: `/credit/${items[0].creditId}/?initial`,
    [NotificationEntityTypes.DENIED]: `/credit/${items[0].creditId}`,
  };

  const cycleEntities = [
    NotificationEntityTypes.CREDIT_CONTACT_NEXT_CYCLE,
    NotificationEntityTypes.CONTACT_NEXT_CYCLE,
  ];

  const goToEntity = async () => {
    try {
      if (cycleEntities.includes(entity)) {
        await adminHttp.readNotification(items[0].id);
      } else {
        await adminHttp.readNotifications(entity);
      }
      redirect(links[entity] as string);
      refreshNotifications();
    } catch (e) {
      notify(axiosErrorToString(e));
    }
  };

  const showInfo = (value: string): JSX.Element => {
    return (
      <>
        <span className={cls.infoTitle}>{value}</span>
        {comment && (
          <span className={cls.infoComment}>&laquo; {comment} &raquo;</span>
        )}
      </>
    );
  };

  return (
    <div
      className={clsx(cls.item, allRead && cls.itemRead)}
      onClick={goToEntity}
    >
      <div className={cls.content}>
        <span className={cls.label}>
          {entity === NotificationEntityTypes.TICKET_CREATED
            && `You have a new ticket from ${ticketFrom}`
          }
          {entity === NotificationEntityTypes.FINANCE_COST_CREATED
            && 'New Expense in Cost Center'
          }
          {entity === NotificationEntityTypes.FINANCE_WITHDRAWAL_CREATED
            && 'New Withdrawal at Finance'
          }
          {entity === NotificationEntityTypes.FINANCE_DEPOSIT_CREATED
            && 'New Deposit at Finance'
          }
          {entity === NotificationEntityTypes.USER_ASSIGNED
            && 'User Assigned'
          }
          {entity === NotificationEntityTypes.USER_CONTACT_OWNER_UPDATED
            && `${items[0].userCode} record assigned to you as “Owner”`}
          {entity === NotificationEntityTypes.CREDIT_FILES_PROGRESS
            && `The credit${items.length === 1 ? ` ${items[0].creditCode}` : 's where'} file is completed`
          }
          {entity === NotificationEntityTypes.CREDIT_STATUS_MODERATION_SIGNED
            && `The credit${items.length === 1 ? ` ${items[0].creditCode}` : 's  where'} status is now "Signed"`
          }
          {entity === NotificationEntityTypes.NEW_CREDIT
            && `New credit${items.length === 1 ? '' : 's'} created`
          }
          {entity === NotificationEntityTypes.NEW_CONTACT_US
            && `New Contact Us ${items.length === 1 ? 'entry' : 'entries'}`
          }
          {entity === NotificationEntityTypes.CREDIT_STATUS_CX
            && `New active credit${items.length === 1 ? ` ${items[0].creditCode}` : 's'}`
          }
          {entity === NotificationEntityTypes.CREDIT_STATUS_DELAY_CX
            && `The credit${items.length === 1 ? ` ${items[0].creditCode}` : 's where status'} is now "Delay"`
          }
          {entity === NotificationEntityTypes.NEW_RESUME
            && `New resume${items.length === 1 ? '' : 's'} received`
          }
          {entity === NotificationEntityTypes.NEW_QUOTATION
            && `New quote${items.length === 1 ? '' : 's'} received`
          }
          {entity === NotificationEntityTypes.INVOICE_UPLOADED
            && `The user${items.length === 1 ? ` ${items[0].userCode} has` : 's have '} uploaded an invoice`
          }
          {entity === NotificationEntityTypes.CALL_OF_THE_DAY
            && `Call${items.length === 1 ? '' : 's'} of the day`
          }
          {entity === NotificationEntityTypes.USER_UPLOADED_DOCUMENTS
            && `The user${items.length === 1 ? ` ${items[0].userCode}` : 's'} uploaded document(s)`
          }
          {entity === NotificationEntityTypes.NEW_PARTNER_REGISTERED
            && `New Partner has created its account: ${partnerName}`
          }
          {entity === NotificationEntityTypes.MODERATION
            && `Credit ${items[0].creditCode} has started application`
          }
          {entity === NotificationEntityTypes.PRE_SCREENING
            && `Sales team requested Pre-Screening for credit ${items[0].creditCode}`
          }
          {entity === NotificationEntityTypes.DOC_COLLECTION
            && `KYC Team requested Doc Collection for credit ${items[0].creditCode}`
          }
          {entity === NotificationEntityTypes.KYC_SCREENING
            && `Sales team requested KYC Screening for credit ${items[0].creditCode}`
          }
          {entity === NotificationEntityTypes.POST_SCREENING
            && `KYC Team Started Post-Screening for credit ${items[0].creditCode}`
          }
          {entity === NotificationEntityTypes.COMPLETED_FILE
            && `KYC determined credit ${items[0].creditCode} as a Completed file`
          }
          {entity === NotificationEntityTypes.BANK_REVISION
            && `Credit ${items[0].creditCode} has been sent to Analysis`
          }
          {entity === NotificationEntityTypes.APPROVED
            && `Credit ${items[0].creditCode} has been approved`
          }
          {entity === NotificationEntityTypes.IN_NEGOTIATIONS
            && `Credit ${items[0].creditCode} has started negotiations`
          }
          {entity === NotificationEntityTypes.CONTRACT_IN_PROCESS
            && `KYC team requested the contract for credit ${items[0].creditCode}`
          }
          {entity === NotificationEntityTypes.SIGNED
            && `¡Credit ${items[0].creditCode} has been signed!`
          }
          {entity === NotificationEntityTypes.REPAID
            && `¡Credit ${items[0].creditCode} has been paid!`
          }
          {entity === NotificationEntityTypes.RESTRUCTURE
            && `Credit ${items[0].creditCode} has been restructured`
          }
          {entity === NotificationEntityTypes.DELAY
            && `Credit ${items[0].creditCode} is delayed`
          }
          {entity === NotificationEntityTypes.DENIED
            && `Credit ${items[0].creditCode} has been rejected`
          }
          {entity === NotificationEntityTypes.CONTACT_NEXT_CYCLE
            && showInfo(`Reminder of ${userCode} – ${userName}`)
          }
          {entity === NotificationEntityTypes.CREDIT_CONTACT_NEXT_CYCLE
            && showInfo(`Reminder of ${creditCode} – ${userName}`)
          }
        </span>
        <span className={cls.date}>{createdAt}</span>
      </div>
    </div>
  );
};
